import { memo, useCallback, useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { ActionNodeData } from '../types';

interface ActionNodeProps {
  data: ActionNodeData;
  selected?: boolean;
}

// Image paths - relative to where the bundle is served (clients/)
const IMAGES = {
  notification: 'images/notificacionicon.png',
  database: 'images/dataicon2.png',
  optional: 'images/optionalicon.png',
  infinite: 'images/loopicon.png',
  camera: 'images/cameraicon.png',
  compact: 'images/compact.png',
  edit: 'images/editicon.png',
  titleHidden: 'images/TituloNoVisible.png',
  groupTitle: 'images/horizontalicon.png',
  groupTogether: 'images/horizontalicon2.png',
  hasImage: 'images/imagesicon.png',
  conditional: 'images/MostrarSiVieneDe.png',
  ia: 'ia/images/luuk-isotipo-sin-ojos.png',
};

// Indicator image component - preserve aspect ratio
const IndicatorImg = ({ src, title, disabled }: { src: string; title: string; disabled?: boolean }) => (
  <img
    src={src}
    title={title}
    style={{
      height: 14,
      width: 'auto',
      maxWidth: 20,
      objectFit: 'contain',
      opacity: disabled ? 0.5 : 1,
    }}
    className={disabled ? 'checkbox-indicator-disabled' : ''}
  />
);

// Flag icon for checkpoint (using SVG since there's no image)
const FlagIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

// Type for alternative options
interface AlternativaOpcion {
  texto: string;
  saltarA?: string;
  finaliza?: boolean;
}

const ActionNode = memo(({ data, selected }: ActionNodeProps) => {
  const {
    label,
    tipo,
    tieneIA,
    iaConfig,
    orientation = 'LR',
    codigoUnico,
    finaliza,
    checkboxes,
    postaGroups,
    conditionalDisplayGroups,
    tipoAccion,
    orden,
    esAlternativa,
  } = data;

  // Get alternative options if this is an alternativa type
  const alternativaOpciones = (data as { alternativaOpciones?: AlternativaOpcion[] }).alternativaOpciones;
  const [isHovered, setIsHovered] = useState(false);

  // Handle move up/down
  const handleMoveUp = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[ActionNode] Dispatching move up for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-move-action', {
        detail: { codigoUnico, direction: 'up' },
      }));
    }
  }, [codigoUnico]);

  const handleMoveDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[ActionNode] Dispatching move down for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-move-action', {
        detail: { codigoUnico, direction: 'down' },
      }));
    }
  }, [codigoUnico]);

  // Handle delete
  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[ActionNode] Dispatching delete for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-delete-action', {
        detail: { codigoUnico },
      }));
    }
  }, [codigoUnico]);

  // Handle duplicate
  const handleDuplicate = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (codigoUnico) {
      console.log('[ActionNode] Dispatching duplicate for:', codigoUnico);
      window.dispatchEvent(new CustomEvent('flow-duplicate-action', {
        detail: { codigoUnico },
      }));
    }
  }, [codigoUnico]);

  // Check if this is a special node that shouldn't have action buttons
  const canModify = tipo !== 'inicio' && tipo !== 'fin';

  // Clean label - remove all emojis and prefixes
  const cleanLabel = label
    .replace(/^[🤖📮⚑📑📁▶✓○◉◫■]\s*/g, '')
    .replace(/\s*✨$/g, '')
    .replace(/^\d+\.\s*/, '');

  // Get accent color based on type
  const getAccentColor = () => {
    if (tieneIA) return '#10b981';
    if (tipo === 'inicio') return '#3b82f6';
    if (tipo === 'fin') return '#ef4444';
    if (tipo === 'grupo') return '#f59e0b';
    if (tipo === 'checkpoint') return '#6366f1';
    if (tipo === 'posta') return '#8b5cf6';
    if (tipo === 'alternativa') return '#ec4899';
    if (tipo === 'seccion') return '#06b6d4';
    return '#d1d5db';
  };

  // Get type label for display
  const getTypeLabel = () => {
    if (tipoAccion?.includes('Checkpoint')) return 'Checkpoint';
    if (tipoAccion?.includes('Posta')) return 'Posta';
    if (tipoAccion?.includes('GrupoDeAcciones')) return 'Grupo';
    if (tipoAccion?.includes('Seccion')) return 'Sección';
    if (tipoAccion?.includes('Alternativa')) return 'Alternativa';
    if (tipoAccion?.includes('Foto')) return 'Foto';
    if (tipoAccion?.includes('Texto')) return 'Texto';
    if (tipoAccion?.includes('Encabezado')) return 'Encabezado';
    if (tipoAccion?.includes('Nota')) return 'Nota';
    if (tipoAccion?.includes('RequiereLocalizacion')) return 'Ubicación';
    if (tipoAccion?.includes('ApruebaExterno')) return 'Aprobación';
    if (tipoAccion?.includes('SolicitudDeArchivo')) return 'Archivo';
    if (tipo === 'inicio') return 'Inicio';
    if (tipo === 'fin') return 'Fin';
    return null;
  };

  const accentColor = getAccentColor();
  const isHorizontal = orientation === 'LR';
  const typeLabel = getTypeLabel();

  // Handle positions based on orientation
  const targetPosition = isHorizontal ? Position.Left : Position.Top;
  const sourcePosition = isHorizontal ? Position.Right : Position.Bottom;

  // Handle styles
  const handleStyle = {
    width: 8,
    height: 8,
    background: '#fff',
    border: '2px solid #d1d5db',
    borderRadius: '50%',
  };

  const targetHandleStyle = {
    ...handleStyle,
    ...(isHorizontal ? { left: -4 } : { top: -4 }),
  };

  const sourceHandleStyle = {
    ...handleStyle,
    ...(isHorizontal ? { right: -4 } : { bottom: -4 }),
  };

  // Node border style - use individual border properties to avoid React warning
  // about mixing shorthand (border) with specific properties (borderLeft/borderTop)
  // IMPORTANT: Keep border width consistent (1px) to prevent dimension changes that cause viewport jumps
  const baseBorderColor = selected ? '#4f46e5' : '#e5e7eb';
  const baseBorder = `1px solid ${baseBorderColor}`;
  const accentBorder = `3px solid ${accentColor}`;

  const nodeBorderStyle = isHorizontal
    ? {
        borderTop: baseBorder,
        borderRight: baseBorder,
        borderBottom: baseBorder,
        borderLeft: accentBorder,
      }
    : {
        borderTop: accentBorder,
        borderRight: baseBorder,
        borderBottom: baseBorder,
        borderLeft: baseBorder,
      };

  // Collect active indicators - using SAME images as old blocks
  const indicators: React.ReactNode[] = [];

  if (checkboxes?.hasNotification) {
    indicators.push(<IndicatorImg key="notif" src={IMAGES.notification} title="Notifica al contestar" />);
  }
  // Show database icon ONLY when checkbox exists AND is NOT checked
  if (checkboxes?.hasDatabaseCheckbox && !checkboxes?.hasDatabase) {
    indicators.push(<IndicatorImg key="db" src={IMAGES.database} title="NO se guarda en base de datos" disabled />);
  }
  if (checkboxes?.isOptional) {
    indicators.push(<IndicatorImg key="opt" src={IMAGES.optional} title="Acción opcional" />);
  }
  if (checkboxes?.isInfinite) {
    indicators.push(<IndicatorImg key="inf" src={IMAGES.infinite} title="Acción infinita" />);
  }
  if (checkboxes?.hasGalleryRestriction) {
    indicators.push(<IndicatorImg key="cam" src={IMAGES.camera} title="Restringir galería" />);
  }
  if (checkboxes?.isCompact) {
    indicators.push(<IndicatorImg key="compact" src={IMAGES.compact} title="Vista compacta" />);
  }
  if (checkboxes?.allEdit) {
    indicators.push(<IndicatorImg key="edit" src={IMAGES.edit} title="Todos pueden editar" />);
  }
  if (checkboxes?.titleHidden) {
    indicators.push(<IndicatorImg key="hide" src={IMAGES.titleHidden} title="Título no visible" />);
  }
  if (checkboxes?.groupTitle) {
    indicators.push(<IndicatorImg key="grptitle" src={IMAGES.groupTitle} title="Título de grupo" />);
  }
  if (checkboxes?.groupTogether) {
    indicators.push(<IndicatorImg key="grptogether" src={IMAGES.groupTogether} title="Grupo juntos" />);
  }
  if (checkboxes?.hasImage) {
    indicators.push(<IndicatorImg key="img" src={IMAGES.hasImage} title="Incluir foto" />);
  }

  return (
    <>
      {/* Input Handle */}
      {tipo !== 'inicio' && (
        <Handle
          type="target"
          position={targetPosition}
          style={targetHandleStyle}
        />
      )}

      {/* Jump-in handle (left side) - target for saltarA jumps */}
      <Handle
        type="target"
        position={Position.Left}
        id="jump-in"
        style={{ width: 6, height: 6, background: 'transparent', border: 'none', left: -3, top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Jump-out handle (right side) - source for saltarA jumps */}
      <Handle
        type="source"
        position={Position.Right}
        id="jump-out"
        style={{ width: 6, height: 6, background: 'transparent', border: 'none', right: -3, top: '50%', transform: 'translateY(-50%)' }}
      />

      {/* Node - Miro style */}
      <div
        style={{
          background: '#fff',
          ...nodeBorderStyle,
          borderRadius: 6,
          padding: '8px 12px',
          minWidth: 140,
          maxWidth: 220,
          boxShadow: selected
            ? '0 0 0 3px rgba(79, 70, 229, 0.1)'
            : '0 1px 3px rgba(0,0,0,0.08)',
          cursor: 'pointer',
          position: 'relative',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Action buttons - appear on hover */}
        {canModify && isHovered && (
          <div
            style={{
              position: 'absolute',
              top: -24,
              right: 4,
              display: 'flex',
              gap: 2,
              background: '#fff',
              borderRadius: 4,
              padding: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              zIndex: 10,
            }}
          >
            <button
              onClick={handleMoveUp}
              onMouseDown={(e) => e.stopPropagation()}
              title="Mover arriba"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#6b7280',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
            <button
              onClick={handleMoveDown}
              onMouseDown={(e) => e.stopPropagation()}
              title="Mover abajo"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#6b7280',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div style={{ width: 1, height: 16, background: '#e5e7eb', margin: '2px 2px' }} />
            <button
              onClick={handleDuplicate}
              onMouseDown={(e) => e.stopPropagation()}
              title="Duplicar"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#6b7280',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </button>
            <button
              onClick={handleDelete}
              onMouseDown={(e) => e.stopPropagation()}
              title="Eliminar"
              style={{
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: 3,
                cursor: 'pointer',
                color: '#ef4444',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        )}

        {/* Header with order number and label */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 6,
          }}
        >
          {/* Order number indicator */}
          {typeof orden === 'number' && orden > 0 && tipo !== 'inicio' && tipo !== 'fin' && (
            <div
              style={{
                minWidth: 18,
                height: 18,
                borderRadius: 3,
                background: accentColor,
                color: '#fff',
                fontSize: 10,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {tipo === 'checkpoint' ? <FlagIcon /> : orden}
            </div>
          )}

          {/* Label text */}
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: '#1f2937',
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              flex: 1,
            }}
          >
            {cleanLabel}
          </div>
        </div>

        {/* Type badge and indicators row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 4,
            marginTop: 6,
          }}
        >
          {/* Type badge */}
          {typeLabel && (
            <div
              style={{
                fontSize: 9,
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                background: '#f3f4f6',
                padding: '1px 5px',
                borderRadius: 3,
              }}
            >
              {typeLabel}
            </div>
          )}

          {/* Checkbox indicators */}
          {indicators.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              {indicators}
            </div>
          )}
        </div>

        {/* IA indicator - using SAME image as old blocks */}
        {tieneIA && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 4,
              padding: '2px 6px',
              background: '#ecfdf5',
              borderRadius: 3,
            }}
          >
            <img
              src={IMAGES.ia}
              style={{
                width: 'auto',
                height: 14,
                objectFit: 'contain',
              }}
              title="IA configurada"
            />
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: '#059669',
                textTransform: 'uppercase',
                letterSpacing: '0.3px',
              }}
            >
              IA
            </span>
            {iaConfig?.tipoAnalisis && (
              <span
                style={{
                  fontSize: 9,
                  color: '#6b7280',
                }}
              >
                {iaConfig.tipoAnalisis}
              </span>
            )}
          </div>
        )}

        {/* Posta groups transfer */}
        {postaGroups && postaGroups.length > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 4,
              padding: '2px 6px',
              background: '#f3e8ff',
              borderRadius: 3,
              fontSize: 9,
              color: '#7c3aed',
            }}
          >
            <span style={{ fontWeight: 600 }}>→</span>
            {postaGroups.join(', ')}
          </div>
        )}

        {/* Conditional display groups - using SAME image */}
        {checkboxes?.showIfComesFrom && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginTop: 4,
              padding: '2px 6px',
              background: '#fce7f3',
              borderRadius: 3,
              fontSize: 9,
              color: '#be185d',
            }}
          >
            <img src={IMAGES.conditional} style={{ width: 14, height: 14 }} title="Mostrar si viene de" />
            {conditionalDisplayGroups && conditionalDisplayGroups.length > 0
              ? conditionalDisplayGroups.join(', ')
              : '(sin grupos)'}
          </div>
        )}

        {/* Alternative options with individual handles */}
        {esAlternativa && alternativaOpciones && alternativaOpciones.length > 0 && (
          <div
            style={{
              marginTop: 4,
              padding: '4px 6px',
              background: '#fef9c3',
              borderRadius: 3,
              fontSize: 10,
              position: 'relative',
            }}
          >
            {alternativaOpciones.map((opt, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  marginBottom: idx < alternativaOpciones.length - 1 ? 2 : 0,
                  position: 'relative',
                }}
              >
                <span style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: opt.saltarA ? '#8b5cf6' : (opt.finaliza ? '#ef4444' : '#94a3b8'),
                  color: '#fff',
                  fontSize: 8,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {String.fromCharCode(65 + idx)}
                </span>
                <span style={{ color: '#713f12', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {opt.texto || `Opción ${idx + 1}`}
                </span>
                {opt.saltarA && <span style={{ color: '#8b5cf6', fontSize: 8 }}>→</span>}
                {opt.finaliza && <span style={{ color: '#ef4444', fontSize: 8 }}>FIN</span>}

                {/* Individual handle for this option if it has a jump - always on right side */}
                {opt.saltarA && (
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`alt-${idx}`}
                    style={{
                      width: 6,
                      height: 6,
                      background: '#8b5cf6',
                      border: '1px solid #6d28d9',
                      borderRadius: '50%',
                      right: -8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      bottom: 'auto',
                      left: 'auto',
                      position: 'absolute',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Finaliza indicator */}
        {finaliza && (
          <div
            style={{
              marginTop: 4,
              padding: '2px 6px',
              background: '#fef2f2',
              borderRadius: 3,
              fontSize: 9,
              color: '#dc2626',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            FIN
          </div>
        )}
      </div>

      {/* Output Handle */}
      {tipo !== 'fin' && (
        <Handle
          type="source"
          position={sourcePosition}
          style={sourceHandleStyle}
        />
      )}
    </>
  );
});

ActionNode.displayName = 'ActionNode';

export default ActionNode;
